/**
 * Button 스타일
 * @description 값은 전부 `--fw-button-*` 변수를 읽는다. 앱 `Button.styles.ts` 의 웹 대응.
 *
 *   변형마다 `--btn-*` 지역 변수만 갈아끼우고, 상태 전환은 base 가 한 벌로 처리한다.
 *   변형별로 hover/active/disabled 클래스를 늘어놓으면 같은 규칙이 7번 복제된다.
 *
 *   테두리는 `border` 가 아니라 inset `box-shadow` 다 — 앱과 같은 이유로, 눌림에서 두께가
 *   1 → 1.5 로 바뀌어도 버튼 크기가 변하면 안 된다. box-shadow 는 레이아웃에 관여하지 않는다.
 *
 *   토큰에 없는 키는 빈 곳이 아니라 스펙이다. `ghost`·`outlined`·`destructiveOutlined` 는
 *   `surface.rest` 가 없어 평상시 투명하고, outlined 계열은 `surface.disabled` 마저 없다.
 */
import { cva } from "class-variance-authority";

export const buttonStyles = cva(
  [
    "inline-flex items-center justify-center font-app",
    "cursor-pointer select-none transition-colors",
    "bg-[var(--btn-surface,transparent)] text-[var(--btn-content)]",
    "shadow-[inset_0_0_0_var(--btn-stroke-width,0)_var(--btn-stroke,transparent)]",
    "hover:enabled:bg-[var(--btn-surface-hovered)]",
    "active:enabled:bg-[var(--btn-surface-pressed)]",
    "active:enabled:shadow-[inset_0_0_0_var(--btn-stroke-width-pressed,0)_var(--btn-stroke-pressed,transparent)]",
    "disabled:cursor-not-allowed",
    "disabled:bg-[var(--btn-surface-disabled,transparent)]",
    "disabled:text-[var(--btn-content-disabled)]",
    "disabled:shadow-[inset_0_0_0_var(--btn-stroke-width,0)_var(--btn-stroke-disabled,transparent)]",
    "focus-visible:outline-2 focus-visible:outline-offset-2",
    "focus-visible:outline-[var(--fw-color-border-focused)]",
  ],
  {
    variants: {
      /** 색 계열. 앱의 `style` prop 인데, 웹에서 `style` 은 이미 예약어라 이름을 바꿨다. */
      tone: {
        neutral: [
          "[--btn-surface:var(--fw-button-color-neutral-surface-rest)]",
          "[--btn-surface-hovered:var(--fw-button-color-neutral-surface-hovered)]",
          "[--btn-surface-pressed:var(--fw-button-color-neutral-surface-pressed)]",
          "[--btn-surface-disabled:var(--fw-button-color-neutral-surface-disabled)]",
          "[--btn-content:var(--fw-button-color-neutral-content-rest)]",
          "[--btn-content-disabled:var(--fw-button-color-neutral-content-disabled)]",
        ],
        ghost: [
          "[--btn-surface-hovered:var(--fw-button-color-ghost-surface-hovered)]",
          "[--btn-surface-pressed:var(--fw-button-color-ghost-surface-pressed)]",
          "[--btn-surface-disabled:var(--fw-button-color-ghost-surface-disabled)]",
          "[--btn-content:var(--fw-button-color-ghost-content-rest)]",
          "[--btn-content-disabled:var(--fw-button-color-ghost-content-disabled)]",
        ],
        outlined: [
          "[--btn-surface-hovered:var(--fw-button-color-outlined-surface-hovered)]",
          "[--btn-surface-pressed:var(--fw-button-color-outlined-surface-pressed)]",
          "[--btn-content:var(--fw-button-color-outlined-content-rest)]",
          "[--btn-content-disabled:var(--fw-button-color-outlined-content-disabled)]",
          "[--btn-stroke:var(--fw-button-color-outlined-stroke-rest)]",
          "[--btn-stroke-pressed:var(--fw-button-color-outlined-stroke-pressed)]",
          "[--btn-stroke-disabled:var(--fw-button-color-outlined-stroke-disabled)]",
          "[--btn-stroke-width:var(--fw-button-stroke-width-rest)]",
          "[--btn-stroke-width-pressed:var(--fw-button-stroke-width-pressed)]",
        ],
        neutralInverse: [
          "[--btn-surface:var(--fw-button-color-neutral-inverse-surface-rest)]",
          "[--btn-surface-hovered:var(--fw-button-color-neutral-inverse-surface-hovered)]",
          "[--btn-surface-pressed:var(--fw-button-color-neutral-inverse-surface-pressed)]",
          "[--btn-surface-disabled:var(--fw-button-color-neutral-inverse-surface-disabled)]",
          "[--btn-content:var(--fw-button-color-neutral-inverse-content-rest)]",
          "[--btn-content-disabled:var(--fw-button-color-neutral-inverse-content-disabled)]",
        ],
        destructive: [
          "[--btn-surface:var(--fw-button-color-destructive-surface-rest)]",
          "[--btn-surface-hovered:var(--fw-button-color-destructive-surface-hovered)]",
          "[--btn-surface-pressed:var(--fw-button-color-destructive-surface-pressed)]",
          "[--btn-surface-disabled:var(--fw-button-color-destructive-surface-disabled)]",
          "[--btn-content:var(--fw-button-color-destructive-content-rest)]",
          "[--btn-content-disabled:var(--fw-button-color-destructive-content-disabled)]",
        ],
        brand: [
          "[--btn-surface:var(--fw-button-color-brand-surface-rest)]",
          "[--btn-surface-hovered:var(--fw-button-color-brand-surface-hovered)]",
          "[--btn-surface-pressed:var(--fw-button-color-brand-surface-pressed)]",
          "[--btn-surface-disabled:var(--fw-button-color-brand-surface-disabled)]",
          "[--btn-content:var(--fw-button-color-brand-content-rest)]",
          "[--btn-content-disabled:var(--fw-button-color-brand-content-disabled)]",
        ],
        destructiveOutlined: [
          "[--btn-surface-hovered:var(--fw-button-color-destructive-outlined-surface-hovered)]",
          "[--btn-surface-pressed:var(--fw-button-color-destructive-outlined-surface-pressed)]",
          "[--btn-content:var(--fw-button-color-destructive-outlined-content-rest)]",
          "[--btn-content-disabled:var(--fw-button-color-destructive-outlined-content-disabled)]",
          "[--btn-stroke:var(--fw-button-color-destructive-outlined-stroke-rest)]",
          "[--btn-stroke-pressed:var(--fw-button-color-destructive-outlined-stroke-pressed)]",
          "[--btn-stroke-disabled:var(--fw-button-color-destructive-outlined-stroke-disabled)]",
          "[--btn-stroke-width:var(--fw-button-stroke-width-rest)]",
          "[--btn-stroke-width-pressed:var(--fw-button-stroke-width-pressed)]",
        ],
      },
      size: {
        sm: [
          "fw-l2",
          "py-[var(--fw-button-sm-padding-vertical)]",
          "px-[var(--fw-button-sm-padding-horizontal)]",
          "gap-[var(--fw-button-sm-gap)]",
        ],
        md: [
          "fw-l1",
          "py-[var(--fw-button-md-padding-vertical)]",
          "px-[var(--fw-button-md-padding-horizontal)]",
          "gap-[var(--fw-button-md-gap)]",
        ],
      },
      radius: {
        square: "rounded-[var(--fw-button-radius-square)]",
        circle: "rounded-[var(--fw-button-radius-circle)]",
      },
      fullWidth: {
        true: "w-full",
        false: "w-fit",
      },
    },
    defaultVariants: {
      tone: "neutral",
      size: "md",
      radius: "square",
      fullWidth: false,
    },
  },
);
